import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { SurveyTemplate } from '../../types/survey';
import * as XLSX from 'xlsx';

interface EditableQuestion {
  id: string;
  category: string;
  question: string;
  purpose: string;
  defaultAnswer: string;
  notes: string;
}

const TemplateEditor: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [template, setTemplate] = useState<SurveyTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'current_state' | 'future_vision'>('current_state');
  const [questions, setQuestions] = useState<EditableQuestion[]>([]);
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Excel import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showSheetSelector, setShowSheetSelector] = useState(false);
  const [excelSheets, setExcelSheets] = useState<string[]>([]);
  const [excelWorkbook, setExcelWorkbook] = useState<XLSX.WorkBook | null>(null);

  useEffect(() => {
    if (templateId) loadTemplate();
  }, [templateId]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getSurveyTemplate(templateId!);
      setTemplate(data);
      setTitle(data.title);
      setType(data.type);
      setQuestions(data.questions.map((q, i) => ({
        id: `q-${i}-${Date.now()}`,
        category: q.category,
        question: q.question,
        purpose: q.purpose || '',
        defaultAnswer: q.defaultAnswer || '',
        notes: q.notes || ''
      })));
    } catch (err) {
      console.error('Failed to load template:', err);
      alert(t('common', 'loadFailed'));
      navigate('/admin/templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert(t('templateEditor', 'titleRequired'));
      return;
    }
    if (questions.length === 0) {
      alert(t('templateEditor', 'questionsRequired'));
      return;
    }
    try {
      setSaving(true);
      await apiClient.updateSurveyTemplate(templateId!, {
        title: title.trim(),
        type,
        questions: questions.map(q => ({
          category: q.category,
          question: q.question,
          purpose: q.purpose,
          defaultAnswer: q.defaultAnswer,
          notes: q.notes
        }))
      });
      setHasChanges(false);
      alert(t('common', 'saved'));
    } catch (err: any) {
      if (err?.response?.data?.error?.code === 'CANNOT_EDIT') {
        alert(t('templateEditor', 'cannotEdit'));
      } else {
        alert(t('common', 'saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (id: string, field: keyof EditableQuestion, value: string) => {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, [field]: value } : q));
    setHasChanges(true);
  };

  const addQuestion = () => {
    const lastCategory = questions.length > 0 ? questions[questions.length - 1].category : '';
    const newQ: EditableQuestion = {
      id: `q-new-${Date.now()}`,
      category: lastCategory,
      question: '',
      purpose: '',
      defaultAnswer: '',
      notes: ''
    };
    setQuestions(prev => [...prev, newQ]);
    setExpandedQuestion(newQ.id);
    setHasChanges(true);
  };

  const deleteQuestion = (id: string) => {
    if (!window.confirm(t('templateEditor', 'deleteQuestionConfirm'))) return;
    setQuestions(prev => prev.filter(q => q.id !== id));
    setHasChanges(true);
  };

  const moveQuestion = (id: string, direction: 'up' | 'down') => {
    const idx = questions.findIndex(q => q.id === id);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === questions.length - 1)) return;
    const newQuestions = [...questions];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [newQuestions[idx], newQuestions[swapIdx]] = [newQuestions[swapIdx], newQuestions[idx]];
    setQuestions(newQuestions);
    setHasChanges(true);
  };

  // Excel import functions
  const handleExcelImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetNames = workbook.SheetNames;

      if (sheetNames.length === 0) {
        alert('Excelファイルにシートがありません');
        return;
      }

      setExcelWorkbook(workbook);
      setExcelSheets(sheetNames);

      if (sheetNames.length === 1) {
        // Single sheet - import directly
        importFromSheet(workbook, sheetNames[0]);
      } else {
        // Multiple sheets - show selector
        setShowSheetSelector(true);
      }
    } catch (err) {
      console.error('Excel read error:', err);
      alert('Excelファイルの読み込みに失敗しました');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const importFromSheet = (workbook: XLSX.WorkBook, sheetName: string) => {
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet);

    if (jsonData.length === 0) {
      alert('シートにデータがありません');
      return;
    }

    // Map Excel columns to question fields
    // Expected columns: カテゴリ/category, 質問/question, 目的/purpose, デフォルト回答/defaultAnswer, 備考/notes
    const importedQuestions: EditableQuestion[] = jsonData.map((row, idx) => ({
      id: `q-import-${Date.now()}-${idx}`,
      category: row['カテゴリ'] || row['category'] || row['Category'] || '',
      question: row['質問'] || row['question'] || row['Question'] || row['質問文'] || '',
      purpose: row['目的'] || row['purpose'] || row['Purpose'] || '',
      defaultAnswer: row['デフォルト回答'] || row['defaultAnswer'] || row['DefaultAnswer'] || row['回答例'] || '',
      notes: row['備考'] || row['notes'] || row['Notes'] || row['ノート'] || ''
    })).filter(q => q.question); // Filter out rows without questions

    if (importedQuestions.length === 0) {
      alert('インポート可能な質問が見つかりません。\n「質問」または「question」列があることを確認してください。');
      return;
    }

    // Confirm overwrite
    if (questions.length > 0) {
      if (!window.confirm(`現在の${questions.length}件の質問を、インポートした${importedQuestions.length}件で上書きしますか？`)) {
        return;
      }
    }

    setQuestions(importedQuestions);
    setHasChanges(true);
    setShowSheetSelector(false);
    setExcelWorkbook(null);
    setExcelSheets([]);

    alert(`${importedQuestions.length}件の質問をインポートしました`);
  };

  const handleSheetSelect = (sheetName: string) => {
    if (excelWorkbook) {
      importFromSheet(excelWorkbook, sheetName);
    }
  };

  const categories = [...new Set(questions.map(q => q.category).filter(Boolean))];

  if (loading) {
    return <div style={{ padding: '30px' }}>{t('common', 'loading')}</div>;
  }

  if (!template) {
    return <div style={{ padding: '30px' }}>{t('templateEditor', 'notFound')}</div>;
  }

  return (
    <div style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>{t('templateEditor', 'title')}</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => navigate('/admin/templates')}
            style={{ padding: '8px 16px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            {t('common', 'back')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            style={{
              padding: '8px 20px',
              backgroundColor: hasChanges ? '#2c5282' : '#a0aec0',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: hasChanges ? 'pointer' : 'not-allowed'
            }}
          >
            {saving ? t('common', 'saving') : t('common', 'save')}
          </button>
        </div>
      </div>

      {/* Basic Info */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem' }}>{t('templateEditor', 'basicInfo')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#4a5568' }}>
              {t('templateUpload', 'titleLabel')}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => { setTitle(e.target.value); setHasChanges(true); }}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#4a5568' }}>
              {t('templateUpload', 'typeLabel')}
            </label>
            <select
              value={type}
              onChange={(e) => { setType(e.target.value as 'current_state' | 'future_vision'); setHasChanges(true); }}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.95rem' }}
            >
              <option value="current_state">{t('template', 'typeCurrentState')}</option>
              <option value="future_vision">{t('template', 'typeFutureVision')}</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: '12px', fontSize: '0.85rem', color: '#718096' }}>
          {questions.length}{t('common', 'questions')} / {categories.length}{t('common', 'categories')}
        </div>
      </div>

      {/* Questions */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{t('templateEditor', 'questions')}</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
            />
            <button
              onClick={handleExcelImport}
              style={{ padding: '6px 14px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              📥 Excelからインポート
            </button>
            <button
              onClick={addQuestion}
              style={{ padding: '6px 14px', backgroundColor: '#48bb78', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              + {t('templateEditor', 'addQuestion')}
            </button>
          </div>
        </div>

        {/* Sheet Selector Modal */}
        {showSheetSelector && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%'
            }}>
              <h3 style={{ margin: '0 0 16px' }}>シートを選択</h3>
              <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '16px' }}>
                インポートするシートを選択してください
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                {excelSheets.map(sheet => (
                  <button
                    key={sheet}
                    onClick={() => handleSheetSelect(sheet)}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.95rem'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    📄 {sheet}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setShowSheetSelector(false);
                  setExcelWorkbook(null);
                  setExcelSheets([]);
                }}
                style={{
                  marginTop: '16px',
                  padding: '8px 16px',
                  backgroundColor: '#edf2f7',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                キャンセル
              </button>
            </div>
          </div>
        )}

        {questions.length === 0 ? (
          <p style={{ color: '#a0aec0', textAlign: 'center', padding: '40px' }}>{t('templateEditor', 'noQuestions')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {questions.map((q, idx) => (
              <div
                key={q.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: expandedQuestion === q.id ? '#f7fafc' : 'white'
                }}
              >
                {/* Question header (collapsed view) */}
                <div
                  onClick={() => setExpandedQuestion(expandedQuestion === q.id ? null : q.id)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <span style={{ color: '#a0aec0', fontSize: '0.85rem', minWidth: '30px' }}>#{idx + 1}</span>
                  {q.category && (
                    <span style={{
                      padding: '2px 8px',
                      backgroundColor: '#ebf8ff',
                      color: '#2c5282',
                      borderRadius: '4px',
                      fontSize: '0.75rem'
                    }}>
                      {q.category}
                    </span>
                  )}
                  <span style={{ flex: 1, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {q.question || <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>{t('templateEditor', 'emptyQuestion')}</span>}
                  </span>
                  <span style={{ color: '#a0aec0', fontSize: '0.8rem' }}>{expandedQuestion === q.id ? '▲' : '▼'}</span>
                </div>

                {/* Expanded editor */}
                {expandedQuestion === q.id && (
                  <div style={{ padding: '0 16px 16px', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#718096' }}>
                          {t('templateEditor', 'category')}
                        </label>
                        <input
                          type="text"
                          value={q.category}
                          onChange={(e) => updateQuestion(q.id, 'category', e.target.value)}
                          list={`categories-${q.id}`}
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }}
                        />
                        <datalist id={`categories-${q.id}`}>
                          {categories.map(c => <option key={c} value={c} />)}
                        </datalist>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#718096' }}>
                          {t('templateEditor', 'purpose')}
                        </label>
                        <input
                          type="text"
                          value={q.purpose}
                          onChange={(e) => updateQuestion(q.id, 'purpose', e.target.value)}
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#718096' }}>
                        {t('templateEditor', 'questionText')}
                      </label>
                      <textarea
                        value={q.question}
                        onChange={(e) => updateQuestion(q.id, 'question', e.target.value)}
                        rows={2}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#718096' }}>
                        {t('templateEditor', 'defaultAnswer')}
                      </label>
                      <textarea
                        value={q.defaultAnswer}
                        onChange={(e) => updateQuestion(q.id, 'defaultAnswer', e.target.value)}
                        rows={2}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', color: '#718096' }}>
                        {t('templateEditor', 'notes')}
                      </label>
                      <textarea
                        value={q.notes}
                        onChange={(e) => updateQuestion(q.id, 'notes', e.target.value)}
                        rows={2}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => moveQuestion(q.id, 'up')}
                          disabled={idx === 0}
                          style={{ padding: '4px 10px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: idx === 0 ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}
                        >
                          ↑ {t('templateEditor', 'moveUp')}
                        </button>
                        <button
                          onClick={() => moveQuestion(q.id, 'down')}
                          disabled={idx === questions.length - 1}
                          style={{ padding: '4px 10px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: idx === questions.length - 1 ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}
                        >
                          ↓ {t('templateEditor', 'moveDown')}
                        </button>
                      </div>
                      <button
                        onClick={() => deleteQuestion(q.id)}
                        style={{ padding: '4px 10px', backgroundColor: '#fff5f5', color: '#e53e3e', border: '1px solid #fed7d7', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                      >
                        {t('common', 'delete')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateEditor;
